import {
  OwcGrid,
  OwcTypography,
  OwcHyperlink,
  OwcButton,
  OwcModalDialog,
  OwcProgressSpinner,
} from "@one/react";
import { useEffect, useState } from "react";
import { chunkArray } from "../../../utils/helpers/text";
import { withApollo } from "react-apollo";
import { BOOKING_GET_CHECKLISTS_FOR_EQUIPMENTS } from "../../../gql/bookingapi";
import {ViewChecklist} from "./ViewChecklist";
import ChecklistFolder from "../../../icons/ChecklistFolder";

const EquipmentDetails = ({ show = true, equipmentData, chunkSize = 6, client }) => {
  const [checklistMap, setChecklistMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedChecklists, setSelectedChecklists] = useState([]);
  const [selectedShortId, setSelectedShortId] = useState("");
  const [selectedEquipment, setSelectedEquipment] = useState(null);

  const bookedEquipments = equipmentData?.bookedEquipments || [];
  const columns = chunkArray(bookedEquipments, chunkSize);

  const truncateText = (text, maxLength = 46) =>
    text && text.length > maxLength ? `${text.substring(0, maxLength - 3)}` : text;

  useEffect(() => {
    if (!equipmentData?.bookingEntries?.length) return;

    const keys = [
      ...new Set(
        equipmentData.bookingEntries
          .map((entry) => {
            const eq = entry.equipment;
            if (!eq?.siteName || !eq?.belongingToGroup || !eq?.equipmentModel) return null;
            return `${eq.siteName}#${eq.belongingToGroup}#${eq.equipmentModel}`;
          })
          .filter(Boolean)
      ),
    ];

    if (!keys.length) return;

    setLoading(true);

    client
      .query({
        query: BOOKING_GET_CHECKLISTS_FOR_EQUIPMENTS,
        variables: { keys },
        fetchPolicy: "no-cache",
      })
      .then((res) => {
        const entries = res?.data?.getChecklistsForEquipments || [];
        const map = Object.fromEntries(entries.map((entry) => [entry.key, entry.checklists]));
        setChecklistMap(map);
      })
      .catch((err) => console.error("Checklist query failed:", err))
      .finally(() => setLoading(false));
  }, [equipmentData, client]);

  if (!show) return null;

  const handleOpenModal = (checklists, shortId) => {
    const foundEntry = equipmentData.bookingEntries.find(
      (entry) => entry.equipment.shortIdentifier === shortId
    );
    setSelectedEquipment(foundEntry?.equipment || null);
    setSelectedChecklists(checklists);
    setSelectedShortId(shortId);
    setModalOpen(true);
  };

  const handleCloseModal = (e) => {
    e.stopPropagation();
    setModalOpen(false);
    setSelectedChecklists([]);
    setSelectedShortId("");
  };

  return (
    <>
      <OwcTypography variant="button" style={{ margin: "1.125rem 0" }}>
        Equipment details
      </OwcTypography>
      <OwcTypography variant="caption" style={{ marginBottom: "6px" }}>Checklists and Short ID(s)</OwcTypography>

      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "120px",
          }}
        >
          <OwcProgressSpinner diameter={60} />
        </div>
      ) : (
        <OwcGrid
          container
          columns={{
            xs: 1,
            s: 1,
            m: 2,
            l: 3,
            xl: 3,
          }}
        >
          {columns.map((column, index) => (
            <OwcGrid
              item
              key={index}
              xl={1}
              l={1}
              m={1}
              xs={1}
              s={1}
              style={{ marginBottom: 12, alignSelf: "start" }}
            >
              {column.map((shortId, i) => {
                const entry = equipmentData.bookingEntries.find(
                  (entry) => entry.equipment.shortIdentifier === shortId
                );
                const eq = entry?.equipment;
                const key = eq && `${eq.siteName}#${eq.belongingToGroup}#${eq.equipmentModel}`;
                const checklists = key ? checklistMap[key] || [] : [];

                return (
                  <div
                    key={i}
                    style={{
                      marginBottom: 8,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {checklists.length === 0 ? (
                      <OwcTypography
                        variant="button"
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          lineHeight: "140%",
                          letterSpacing: "0%",
                          display: "inline-flex",
                          alignItems: "center",
                          verticalAlign: "middle",
                          fontFamily: "inherit",
                        }}
                      >
                        {truncateText(shortId, 50)}
                      </OwcTypography>
                    ) : checklists.length === 1 ? (
                      <OwcHyperlink
                        href={checklists[0].url}
                        target="_blank"
                        title={checklists[0]?.description || "Open checklist"}
                        rel="noopener noreferrer"
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          fontFamily: "inherit",
                          lineHeight: "140%",
                          letterSpacing: "0%",
                          display: "inline-flex",
                          alignItems: "center",
                          verticalAlign: "middle",
                          color: "#312f2e"
                        }}
                      >
                        <ChecklistFolder size={18} />
                        &nbsp;{truncateText(shortId)}
                      </OwcHyperlink>
                    ) : (
                      <button
                        onClick={() => handleOpenModal(checklists, shortId)}
                        title={`Show checklists for ${shortId}`}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                          fontWeight: 600,
                          fontFamily: "inherit",
                          fontSize: 14,
                          lineHeight: "140%",
                          letterSpacing: "0%",
                          display: "inline-flex",
                          alignItems: "center",
                          verticalAlign: "middle",
                          color: "#312f2e"
                        }}
                      >
                        <ChecklistFolder size={18} />
                        &nbsp;{truncateText(shortId)}
                      </button>
                    )}
                  </div>
                );
              })}
            </OwcGrid>
          ))}
        </OwcGrid>
      )}

      <OwcModalDialog
        disableScrollLock
        visible={modalOpen}
        size="m"
        className="owcmodalZIndex"
        onVisibleChange={(e) => {
          e.stopPropagation();
          if (!e.target.visible) handleCloseModal(e);
        }}
      >
        <div slot="header">
          <OwcTypography variant="button">
            Checklists for {selectedShortId}
          </OwcTypography>
        </div>

        <ViewChecklist
          equipmentDetails={selectedEquipment}
          checklists={selectedChecklists}
          loading={loading}
          minHeight={200}
        />

        <div slot="actions" style={{ marginLeft: "auto", display: "flex", justifyContent: "end" }}>
          <OwcButton variant="secondary" onClick={handleCloseModal}>
            Close
          </OwcButton>
        </div>
      </OwcModalDialog>
    </>
  );
};

export default withApollo(EquipmentDetails);
